import { state } from '__common/store';
import { lat_lng_distance_to_feet } from '__editor/panelsEditor/components/background/utils/backgroundCordinatesConverter';

// Two corners are treated as the same point (a shared corner) when they sit
// within this real-world distance of each other. Drawing snaps corners exactly
// on top of one another, but the pixel <-> lat/lng round trip can introduce a
// sub-inch drift, so a small tolerance keeps the detection reliable.
export const SHARED_CORNER_TOLERANCE_FT = 0.5;

type RoofsMap = { [roofId: string]: drawingManagerRoof };

function cornersAreShared(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): boolean {
  return lat_lng_distance_to_feet(a, b) <= SHARED_CORNER_TOLERANCE_FT;
}

// Two roofs belong to the same consolidated structure when they share at least
// one corner. Because the drawing tool snaps a new corner onto the start/end
// point of an existing roof edge, a shared corner reliably implies a shared edge.
export function roofsShareCorner(roofA: drawingManagerRoof, roofB: drawingManagerRoof): boolean {
  if (!roofA || !roofB || !roofA.coords || !roofB.coords) {
    return false;
  }

  return roofA.coords.some(cornerA =>
    roofB.coords.some(cornerB => cornersAreShared(cornerA, cornerB)),
  );
}

// Groups roofs into consolidated structures using a union-find over "shares a
// corner" adjacency. Returns an array of groups, each group being an array of
// roof ids. Roofs that touch nothing come back as their own single-item group.
export function getConsolidatedRoofGroups(roofs?: RoofsMap): number[][] {
  const roofsMap = roofs || state().drawingManager.roofs;

  if (!roofsMap || !Object.keys(roofsMap).length) {
    return [];
  }

  const roofIds = Object.keys(roofsMap).map(Number);
  const parent: { [id: number]: number } = {};
  roofIds.forEach(id => (parent[id] = id));

  const find = (id: number): number => {
    while (parent[id] !== id) {
      parent[id] = parent[parent[id]];
      id = parent[id];
    }
    return id;
  };

  const union = (a: number, b: number) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) {
      parent[rootB] = rootA;
    }
  };

  for (let i = 0; i < roofIds.length; i++) {
    for (let j = i + 1; j < roofIds.length; j++) {
      if (roofsShareCorner(roofsMap[roofIds[i]], roofsMap[roofIds[j]])) {
        union(roofIds[i], roofIds[j]);
      }
    }
  }

  const groups: { [root: number]: number[] } = {};
  roofIds.forEach(id => {
    const root = find(id);
    if (!groups[root]) {
      groups[root] = [];
    }
    groups[root].push(id);
  });

  return Object.keys(groups).map(root => groups[Number(root)].sort((a, b) => a - b));
}

// Returns only the consolidated groups that actually contain more than one roof
// (i.e. the ones that share geometry and warrant a combined view/DXF).
export function getSharedRoofGroups(roofs?: RoofsMap): number[][] {
  return getConsolidatedRoofGroups(roofs).filter(group => group.length > 1);
}
